import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import { ChevronLeft, ChevronRight, Calendar, Video, MapPin, Briefcase, Users, RefreshCw } from 'lucide-react';
import { companyApi } from '../../../lib/api';
import { toast } from 'sonner';

const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function getMondayOfWeek(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatYMD(d) {
  return d.toISOString().slice(0, 10);
}

function formatDayLabel(ymd) {
  const d = new Date(ymd + 'T12:00:00');
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function isToday(ymd) {
  return ymd === formatYMD(new Date());
}

const STATUS_BADGE = {
  pending: 'bg-orange-100 text-orange-700',
  confirmed: 'bg-emerald-100 text-emerald-700',
  approved: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-blue-100 text-blue-700',
};

export function RdvPlanningPage() {
  const [weekStart, setWeekStart] = useState(getMondayOfWeek());
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterMode, setFilterMode] = useState('all'); // 'all' | 'physical' | 'video'

  const fetchData = async (date) => {
    setIsLoading(true);
    try {
      const res = await companyApi.getRdvPlanning(formatYMD(date));
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(weekStart);
  }, [weekStart]); // eslint-disable-line

  const goPrev = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d);
  };
  const goNext = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d);
  };
  const goToday = () => setWeekStart(getMondayOfWeek());

  // Filter appointments by mode
  const filterAppt = (a) => {
    if (filterMode === 'all') return true;
    const isVideo = a.meeting_type === 'online' || a.meeting_type === 'video';
    return filterMode === 'video' ? isVideo : !isVideo;
  };

  // Stats
  const stats = useMemo(() => {
    if (!data) return { total: 0, video: 0, physical: 0, unassigned: 0 };
    const all = [
      ...data.commercials.flatMap(c => c.appointments),
      ...(data.unassigned || [])
    ];
    return {
      total: all.length,
      video: all.filter(a => a.meeting_type === 'online' || a.meeting_type === 'video').length,
      physical: all.filter(a => a.meeting_type !== 'online' && a.meeting_type !== 'video').length,
      unassigned: (data.unassigned || []).length,
    };
  }, [data]);

  const weekLabel = useMemo(() => {
    if (!data) return '';
    const start = new Date(data.week_start);
    const end = new Date(data.week_end);
    end.setDate(end.getDate() - 1);
    const fmt = (d) => d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    const yearLabel = end.getFullYear();
    return `${fmt(start)} → ${fmt(end)} ${yearLabel}`;
  }, [data]);

  return (
    <div className="space-y-6" data-testid="rdv-planning-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-semibold flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center">
              <Briefcase className="w-5 h-5" />
            </span>
            Planning RDV
          </h1>
          <p className="text-muted-foreground mt-1">
            Vue par commercial des rendez-vous physiques et visio
          </p>
        </div>
        <Button variant="outline" onClick={() => fetchData(weekStart)} data-testid="rdv-planning-refresh">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total RDV</p>
            <p className="text-2xl font-bold mt-1">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50/40">
          <CardContent className="p-4">
            <p className="text-xs text-blue-700 uppercase tracking-wider flex items-center gap-1.5">
              <MapPin className="w-3 h-3" />
              Physique
            </p>
            <p className="text-2xl font-bold mt-1 text-blue-700">{stats.physical}</p>
          </CardContent>
        </Card>
        <Card className="bg-indigo-50/40">
          <CardContent className="p-4">
            <p className="text-xs text-indigo-700 uppercase tracking-wider flex items-center gap-1.5">
              <Video className="w-3 h-3" />
              Visio
            </p>
            <p className="text-2xl font-bold mt-1 text-indigo-700">{stats.video}</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50/40">
          <CardContent className="p-4">
            <p className="text-xs text-amber-700 uppercase tracking-wider">Non affectés</p>
            <p className="text-2xl font-bold mt-1 text-amber-700">{stats.unassigned}</p>
          </CardContent>
        </Card>
      </div>

      {/* Week navigator + filter pills */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goPrev} data-testid="week-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={goToday} data-testid="week-today">
            <Calendar className="w-4 h-4 mr-2" />
            Aujourd'hui
          </Button>
          <Button variant="outline" size="icon" onClick={goNext} data-testid="week-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <span className="ml-3 font-semibold text-lg">{weekLabel}</span>
        </div>

        <div className="flex gap-2">
          {[
            { v: 'all', label: 'Tous', icon: Users },
            { v: 'physical', label: 'Physique', icon: MapPin },
            { v: 'video', label: 'Visio', icon: Video },
          ].map(({ v, label, icon: Icon }) => (
            <button
              key={v}
              type="button"
              onClick={() => setFilterMode(v)}
              data-testid={`rdv-filter-${v}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                filterMode === v
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-white border-border hover:border-foreground/20'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Calendar grid (swimlanes) */}
      {isLoading ? (
        <Card>
          <CardContent className="p-6 space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </CardContent>
        </Card>
      ) : data ? (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-3 w-[200px] border-r border-border font-semibold uppercase text-xs tracking-wider text-muted-foreground">
                    Commercial
                  </th>
                  {data.days.map((day, idx) => (
                    <th
                      key={day}
                      className={`text-center px-2 py-3 border-r border-border last:border-r-0 ${
                        isToday(day) ? 'bg-primary/10' : ''
                      }`}
                    >
                      <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        {DAY_LABELS[idx]}
                      </div>
                      <div className={`font-semibold mt-0.5 ${isToday(day) ? 'text-primary' : 'text-foreground'}`}>
                        {formatDayLabel(day)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.commercials.map((c) => {
                  const filtered = c.appointments.filter(filterAppt);
                  return (
                    <tr key={c.id} className="border-t border-border" data-testid={`commercial-row-${c.id}`}>
                      <td className="px-4 py-3 align-top border-r border-border">
                        <div className="flex items-start gap-2">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-semibold ${
                            c.role === 'commercial' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {(c.name || '?').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold leading-tight">{c.name}</p>
                            {c.role === 'commercial' && (
                              <span className="text-[10px] uppercase font-bold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded mt-1 inline-block">
                                Commercial
                              </span>
                            )}
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {filtered.length} RDV
                            </p>
                          </div>
                        </div>
                      </td>
                      {data.days.map((day) => {
                        const dayAppts = filtered.filter(a => a.date === day);
                        return (
                          <td key={day} className={`px-1.5 py-2 align-top border-r border-border last:border-r-0 min-w-[140px] ${isToday(day) ? 'bg-primary/5' : ''}`}>
                            <div className="space-y-1">
                              {dayAppts.length === 0 ? (
                                <div className="h-8" />
                              ) : (
                                dayAppts.map((a) => {
                                  const isVideo = a.meeting_type === 'online' || a.meeting_type === 'video';
                                  return (
                                    <div
                                      key={a.id}
                                      data-testid={`appt-card-${a.id}`}
                                      className={`rounded-lg p-2 text-xs cursor-pointer hover:shadow-md transition-shadow border ${
                                        isVideo
                                          ? 'bg-indigo-50 border-indigo-200 hover:border-indigo-400'
                                          : 'bg-blue-50 border-blue-200 hover:border-blue-400'
                                      }`}
                                    >
                                      <div className="flex items-center gap-1 font-semibold">
                                        {isVideo ? <Video className="w-3 h-3 text-indigo-600" /> : <MapPin className="w-3 h-3 text-blue-600" />}
                                        <span>{a.start_time || a.time_slot}</span>
                                      </div>
                                      <p className="font-medium text-foreground truncate mt-0.5">
                                        {a.client_name}
                                      </p>
                                      <p className="text-[10px] text-muted-foreground truncate">
                                        {a.duration_minutes || a.duration || 60} min
                                      </p>
                                      {isVideo && !a.meeting_link && (
                                        <Badge className="bg-red-100 text-red-700 text-[9px] px-1 py-0 mt-1 border-0">Lien manquant</Badge>
                                      )}
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Unassigned bucket */}
          {(data.unassigned || []).length > 0 && (
            <div className="border-t border-border p-4 bg-amber-50/40">
              <h3 className="text-sm font-semibold text-amber-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Non affectés ({data.unassigned.length})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {data.unassigned.filter(filterAppt).map((a) => {
                  const isVideo = a.meeting_type === 'online' || a.meeting_type === 'video';
                  return (
                    <div key={a.id} className="rounded-lg p-2 text-xs bg-white border border-amber-200">
                      <div className="flex items-center gap-1 font-semibold">
                        {isVideo ? <Video className="w-3 h-3 text-indigo-600" /> : <MapPin className="w-3 h-3 text-blue-600" />}
                        <span>{a.date} · {a.start_time || a.time_slot}</span>
                      </div>
                      <p className="font-medium text-foreground truncate mt-0.5">{a.client_name}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      ) : null}
    </div>
  );
}

export default RdvPlanningPage;
